import React, { useState, useEffect } from "react";
import {
  ExternalLink,
  Download,
  Copy,
  Check,
  FileText,
  AlertCircle,
  FileCode,
  Calendar,
  Scale,
  User,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  useGetCourtDocumentMediaQuery,
  type DocumentRow,
} from "@/redux/api/courtScraper.rtkq";

// ── PDF Viewer Modal ──────────────────────────────────────────────────────────

interface PdfModalProps {
  document: DocumentRow | null;
  isOpen: boolean;
  onClose: () => void;
}

export const PdfViewerModal: React.FC<PdfModalProps> = ({
  document,
  isOpen,
  onClose,
}) => {
  const { data, isLoading, isError } = useGetCourtDocumentMediaQuery(
    {
      gcsBucket: document?.gcsBucket,
      gcsPath: document?.gcsPath,
      consolidatedGcsPath: document?.consolidatedGcsPath,
      filename: document?.filename || "",
      caseName: document?.caseName,
    },
    { skip: !isOpen || !document }
  );

  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (data?.pdfSignedUrl) {
      setBlobUrl(null);
      return;
    }
    if (data?.pdfBase64) {
      try {
        const byteCharacters = atob(data.pdfBase64);
        const byteNumbers = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([byteNumbers], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        return () => {
          URL.revokeObjectURL(url);
        };
      } catch (err) {
        console.error("Failed to convert PDF base64 to blob URL:", err);
      }
    }
    setBlobUrl(null);
  }, [data?.pdfSignedUrl, data?.pdfBase64]);

  const activePdfUrl = data?.pdfSignedUrl || blobUrl;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-4xl w-[95vw] h-[90vh] flex flex-col p-0 overflow-hidden bg-white"
        dir="rtl"
      >
        <DialogHeader className="p-4 border-b border-slate-100 flex flex-row items-center justify-between">
          <div className="space-y-1">
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              <span>מסמך מקור (PDF): {document?.caseDisplayIdentifier || document?.filename}</span>
            </DialogTitle>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              {document?.caseName && <span>{document.caseName}</span>}
              {document?.courtName && <Badge variant="outline">{document.courtName}</Badge>}
              {document?.decisionTypeName && (
                <Badge variant="secondary">{document.decisionTypeName}</Badge>
              )}
            </div>
          </div>
          {activePdfUrl && (
            <div className="flex items-center gap-2 pl-6">
              <Button
                variant="outline"
                size="sm"
                className="text-xs flex items-center gap-1.5"
                onClick={() => window.open(activePdfUrl, "_blank")}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                פתח בלשונית חדשה
              </Button>
              <Button
                size="sm"
                className="text-xs bg-indigo-600 hover:bg-indigo-700 flex items-center gap-1.5 text-white"
                asChild
              >
                <a href={activePdfUrl} download={`${document?.filename}.pdf`}>
                  <Download className="w-3.5 h-3.5" />
                  הורד קובץ
                </a>
              </Button>
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 w-full h-full bg-slate-100 flex items-center justify-center relative">
          {isLoading && (
            <div className="flex flex-col items-center gap-3 text-slate-500">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm">מייצר קישור מאובטח לקובץ ה-PDF מ-GCS...</span>
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center gap-2 text-rose-600 p-6 text-center">
              <AlertCircle className="w-8 h-8" />
              <span className="font-semibold text-sm">שגיאה בהשגת קובץ ה-PDF</span>
              <span className="text-xs text-slate-500">
                ייתכן והקובץ טרם ירד מנט המשפט או שאינו זמין ב-GCS.
              </span>
            </div>
          )}

          {!isLoading && !isError && activePdfUrl && (
            <iframe
              src={`${activePdfUrl}#toolbar=1`}
              className="w-full h-full border-0"
              title="PDF Preview"
            />
          )}

          {!isLoading && !isError && !activePdfUrl && (
            <div className="flex flex-col items-center gap-2 text-slate-500 p-6 text-center">
              <FileText className="w-10 h-10 text-slate-300" />
              <span className="font-semibold text-sm">קובץ PDF אינו זמין</span>
              <span className="text-xs text-slate-400">
                מסמך זה נמצא בסטטוס: {document?.downloadStatus}
              </span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── Extracted Text & JSON Modal ───────────────────────────────────────────────

interface ExtractedTextModalProps {
  document: DocumentRow | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ExtractedTextModal: React.FC<ExtractedTextModalProps> = ({
  document,
  isOpen,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<"formatted" | "raw">("formatted");

  const { data, isLoading, isError } = useGetCourtDocumentMediaQuery(
    {
      gcsBucket: document?.gcsBucket,
      gcsPath: document?.gcsPath,
      consolidatedGcsPath: document?.consolidatedGcsPath,
      filename: document?.filename || "",
      caseName: document?.caseName,
    },
    { skip: !isOpen || !document }
  );

  const extracted = data?.extractedJson;

  const handleCopy = () => {
    if (!extracted) return;
    navigator.clipboard.writeText(JSON.stringify(extracted, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-4xl w-[95vw] h-[85vh] flex flex-col p-0 overflow-hidden bg-white"
        dir="rtl"
      >
        <DialogHeader className="p-4 border-b border-slate-100 flex flex-row items-center justify-between">
          <div className="space-y-1">
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <FileCode className="w-5 h-5 text-indigo-600" />
              <span>תוכן שחולץ ע"י Gemini: {document?.caseDisplayIdentifier || document?.filename}</span>
            </DialogTitle>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              {document?.caseName && <span>{document.caseName}</span>}
              {document?.splitPageCount && (
                <Badge variant="outline">{document.splitPageCount} עמודים חולצו</Badge>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 pl-6">
            <div className="bg-slate-100 p-1 rounded-md flex text-xs">
              <button
                className={`px-3 py-1 rounded transition-colors ${
                  activeTab === "formatted"
                    ? "bg-white text-indigo-600 font-semibold shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("formatted")}
              >
                תצוגה מעוצבת
              </button>
              <button
                className={`px-3 py-1 rounded transition-colors ${
                  activeTab === "raw"
                    ? "bg-white text-indigo-600 font-semibold shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("raw")}
              >
                JSON גולמי
              </button>
            </div>

            {extracted && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs flex items-center gap-1.5"
                onClick={handleCopy}
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "הועתק!" : "העתק JSON"}
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5 bg-slate-50">
          {isLoading && (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-500 py-20">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm">טוען קובץ extracted.json מ-GCS...</span>
            </div>
          )}

          {isError && (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-rose-600 p-6 text-center py-20">
              <AlertCircle className="w-8 h-8" />
              <span className="font-semibold text-sm">שגיאה בטעינת תוצאות החילוץ</span>
              <span className="text-xs text-slate-500">
                ייתכן ששלב האיחוד (consolidation) טרם הושלם למסמך זה.
              </span>
            </div>
          )}

          {!isLoading && !isError && !extracted && (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-slate-500 p-6 text-center py-20">
              <FileCode className="w-10 h-10 text-slate-300" />
              <span className="font-semibold text-sm">טרם נוצר קובץ extracted.json</span>
              <span className="text-xs text-slate-400">
                סטטוס איחוד נוכחי: {document?.consolidationStatus}
              </span>
            </div>
          )}

          {!isLoading && !isError && extracted && (
            <>
              {activeTab === "formatted" ? (
                <div className="space-y-4 max-w-3xl mx-auto">
                  {/* Meta Info Card */}
                  <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm space-y-3">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      פרטי פסק הדין / ההחלטה
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                      <div className="flex items-center gap-2 text-slate-700">
                        <Scale className="w-4 h-4 text-indigo-500 shrink-0" />
                        <span>{extracted.court || document?.courtName || "לא צוין"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-700">
                        <User className="w-4 h-4 text-indigo-500 shrink-0" />
                        <span>{extracted.judge || document?.judgeName || "לא צוין"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-700">
                        <Calendar className="w-4 h-4 text-indigo-500 shrink-0" />
                        <span>{extracted.decisionDate || document?.decisionSignatureDate || "לא צוין"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Header / Summary Card */}
                  {extracted.header && (
                    <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm space-y-2">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        כותרת ופתיח
                      </div>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                        {extracted.header}
                      </p>
                    </div>
                  )}

                  {/* Extracted Text Body */}
                  <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm space-y-2">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      תוכן ההחלטה המלא שחולץ
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed max-h-[450px] overflow-y-auto p-3 bg-slate-50 rounded border border-slate-100 font-sans">
                      {extracted.fullText ||
                        extracted.text ||
                        (Array.isArray(extracted.pages)
                          ? extracted.pages.map((p: any, i: number) => `--- עמוד ${i + 1} ---\n${p.text || p.content || ""}`).join("\n\n")
                          : JSON.stringify(extracted, null, 2))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-900 text-slate-100 p-4 rounded-lg font-mono text-xs overflow-x-auto max-h-[600px]">
                  <pre>{JSON.stringify(extracted, null, 2)}</pre>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
